import {GenericSidebarItemBadgeComponent} from '@/components/_domain/SidebarItemBadges/SidebarItemBadge';
import {
  ComponentType,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useLocation} from 'react-router-dom';
import {SidebarCategory} from './types';

import {DateTime} from 'luxon';

export type SidebarItem = {
  label: string;
  icon: ReactNode;
  url: string;
  id?: string;
  BadgeComponent?: GenericSidebarItemBadgeComponent;
  CountComponent?: ComponentType;
  opensRoot?: boolean;
  type?: 'item' | 'divider';
  releaseDate?: DateTime;
  sectionTitle?: string;
  isActive?: boolean;
};

type SidebarRouteOverride = {
  category: SidebarCategory;
  activeItem?: string;
};

type SidebarContextType = {
  selectedCategory: SidebarCategory | null;
  setSelectedCategory: (category: SidebarCategory) => void;
  getItemsForCategory: (category: SidebarCategory) => SidebarItem[];
  isSubSidebarCollapsed: boolean;
  setSubSidebarCollapsed: (collapsed: boolean) => void;
  toggleSubSidebar: () => void;
  isSidebarPinned: boolean;
  setSidebarPinned: (pinned: boolean) => void;
  toggleSidebarPin: () => void;
  activeItemOverride: string | null;
  setSidebarRoute: (override: SidebarRouteOverride | null) => void;
};

type SidebarProviderProps = {
  children: ReactNode;
  categoryItems: Map<SidebarCategory, SidebarItem[]>;
  defaultCategory?: SidebarCategory;
};

const STORAGE_KEY_COLLAPSED = 'ark_subSidebar_collapsed';
const STORAGE_KEY_SIDEBAR_PINNED = 'ark_sidebar_pinned';

const SidebarContext = createContext<SidebarContextType | null>(null);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

const getCategoryFromPath = (
  pathname: string,
  categoryItems: Map<SidebarCategory, SidebarItem[]>,
): SidebarCategory | null => {
  let bestMatch: {category: SidebarCategory; pathLength: number} | null = null;

  for (const [category, items] of categoryItems.entries()) {
    for (const item of items) {
      const itemPath = item.url.split('?')[0];
      if (pathname.startsWith(itemPath) && itemPath.length > 0) {
        if (!bestMatch || itemPath.length > bestMatch.pathLength) {
          bestMatch = {category, pathLength: itemPath.length};
        }
      }
    }
  }

  return bestMatch?.category ?? null;
};

export const SidebarProvider = ({
  children,
  categoryItems,
  defaultCategory,
}: SidebarProviderProps) => {
  const location = useLocation();
  const category = getCategoryFromPath(location.pathname, categoryItems);

  const [selectedCategory, setSelectedCategoryState] =
    useState<SidebarCategory | null>(category ?? defaultCategory ?? null);

  const [prevPathname, setPrevPathname] = useState(location.pathname);
  const [prevCategoryItems, setPrevCategoryItems] = useState(categoryItems);

  const routeOverrideRef = useRef<SidebarRouteOverride | null>(null);
  const [activeItemOverride, setActiveItemOverride] = useState<string | null>(
    null,
  );
  const locationRef = useRef(location.pathname);
  const categoryItemsRef = useRef(categoryItems);
  locationRef.current = location.pathname;
  categoryItemsRef.current = categoryItems;

  if (
    location.pathname !== prevPathname ||
    categoryItems !== prevCategoryItems
  ) {
    setPrevPathname(location.pathname);
    setPrevCategoryItems(categoryItems);
    if (routeOverrideRef.current) {
      setSelectedCategoryState(routeOverrideRef.current.category);
    } else {
      const categoryFromPath = getCategoryFromPath(
        location.pathname,
        categoryItems,
      );
      if (categoryFromPath) {
        setSelectedCategoryState(categoryFromPath);
      }
    }
  }

  const [isSubSidebarCollapsed, setIsSubSidebarCollapsed] = useState<boolean>(
    () => {
      const stored = localStorage.getItem(STORAGE_KEY_COLLAPSED);
      return stored ? JSON.parse(stored) : false;
    },
  );

  const [isSidebarPinned, setIsSidebarPinned] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_SIDEBAR_PINNED);
    return stored ? JSON.parse(stored) : true;
  });

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY_COLLAPSED,
      JSON.stringify(isSubSidebarCollapsed),
    );
  }, [isSubSidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY_SIDEBAR_PINNED,
      JSON.stringify(isSidebarPinned),
    );
  }, [isSidebarPinned]);

  const setSelectedCategory = useCallback((category: SidebarCategory) => {
    routeOverrideRef.current = null;
    setActiveItemOverride(null);
    setSelectedCategoryState(category);
  }, []);

  const setSidebarRoute = useCallback(
    (override: SidebarRouteOverride | null) => {
      routeOverrideRef.current = override;
      if (override) {
        setSelectedCategoryState(override.category);
        setActiveItemOverride(override.activeItem ?? null);
      } else {
        setActiveItemOverride(null);
        const categoryFromPath = getCategoryFromPath(
          locationRef.current,
          categoryItemsRef.current,
        );
        if (categoryFromPath) {
          setSelectedCategoryState(categoryFromPath);
        }
      }
    },
    [],
  );

  const setSubSidebarCollapsed = useCallback((collapsed: boolean) => {
    setIsSubSidebarCollapsed(collapsed);
  }, []);

  const toggleSubSidebar = useCallback(() => {
    setIsSubSidebarCollapsed(prev => !prev);
  }, []);

  const setSidebarPinned = useCallback((pinned: boolean) => {
    setIsSidebarPinned(pinned);
  }, []);

  const toggleSidebarPin = useCallback(() => {
    setIsSidebarPinned(prev => !prev);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;

      const target = event.target as HTMLElement;
      const isInputElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (isInputElement) return;

      if (event.key === '[' && !event.shiftKey) {
        event.preventDefault();
        setIsSidebarPinned(prev => !prev);
      }

      if (event.key === '{' || (event.key === '[' && event.shiftKey)) {
        event.preventDefault();
        setIsSubSidebarCollapsed(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getItemsForCategory = useCallback(
    (category: SidebarCategory): SidebarItem[] => {
      return categoryItems.get(category) ?? [];
    },
    [categoryItems],
  );

  return (
    <SidebarContext.Provider
      value={{
        selectedCategory,
        setSelectedCategory,
        getItemsForCategory,
        isSubSidebarCollapsed,
        setSubSidebarCollapsed,
        toggleSubSidebar,
        isSidebarPinned,
        setSidebarPinned,
        toggleSidebarPin,
        activeItemOverride,
        setSidebarRoute,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
